import type {
  AdminAuditLogListResponseData,
  AdminAuthenticatedSession,
  AdminBookingDetailResponseData,
  AdminBookingListResponseData,
  AdminCreditAdjustmentRequest,
  AdminCreditAdjustmentResponseData,
  AdminCriticalActionRequest,
  AdminDashboardSummaryResponseData,
  AdminLoginRequest,
  AdminLoginResponseData,
  AdminLogoutRequest,
  AdminMfaBackupVerifyRequest,
  AdminMfaSetupBeginResponseData,
  AdminMfaSetupConfirmRequest,
  AdminMfaSetupConfirmResponseData,
  AdminMfaVerifyRequest,
  AdminProductListResponseData,
  AdminProfileResponseData,
  AdminRefreshRequest,
  AdminRefreshResponseData,
  AdminReportDetailResponseData,
  AdminReportListResponseData,
  AdminStoreDetailResponseData,
  AdminStoreListResponseData,
  AdminUserListResponseData,
  AdminUserSummary,
  AdminWebhookEventListResponseData,
  ApiSuccessResponse,
  AuthActionAcceptedResponseData,
  ContentItemListResponseData,
  ContentItemResponseData,
  CreateContentItemRequest,
  CreditPackageResponseData,
  PromotionServiceResponseData,
  RequestContentMediaUploadUrlRequest,
  RequestContentMediaUploadUrlResponseData,
  SubscriptionPlanResponseData,
  UpdateContentItemRequest,
} from "@chaochud/contracts";
import { createApiClient } from "@chaochud/api-client";

import { getAdminAccessToken } from "./auth/token-storage";

/**
 * Admin API base — Vite env (`VITE_ADMIN_API_URL`), never bundled into apps/web.
 * Separate session keys live in token-storage.ts (AGENTS.md §4).
 */
const ADMIN_API_BASE_URL = `${
  import.meta.env.VITE_ADMIN_API_URL ?? "http://localhost:4000"
}/api/v1`;

function getAuthHeaders(): Record<string, string> {
  const accessToken = getAdminAccessToken();
  return accessToken ? { Authorization: `Bearer ${accessToken}` } : {};
}

const client = createApiClient({ baseUrl: ADMIN_API_BASE_URL, getHeaders: getAuthHeaders });

async function unwrap<TResponse>(path: string, init?: RequestInit): Promise<TResponse> {
  const response = await client.request<ApiSuccessResponse<TResponse>>(path, init);
  return response.data;
}

function qs(params: Record<string, string | number | undefined>): string {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined && value !== "") search.set(key, String(value));
  }
  const query = search.toString();
  return query ? `?${query}` : "";
}

export const adminAuthClient = {
  login: (payload: AdminLoginRequest) =>
    unwrap<AdminLoginResponseData>("/admin/auth/login", {
      method: "POST",
      body: JSON.stringify(payload),
    }),
  verifyMfa: (payload: AdminMfaVerifyRequest) =>
    unwrap<AdminAuthenticatedSession>("/admin/auth/mfa/verify", {
      method: "POST",
      body: JSON.stringify(payload),
    }),
  verifyMfaBackup: (payload: AdminMfaBackupVerifyRequest) =>
    unwrap<AdminAuthenticatedSession>("/admin/auth/mfa/verify-backup", {
      method: "POST",
      body: JSON.stringify(payload),
    }),
  beginMfaSetup: (mfaToken: string) =>
    unwrap<AdminMfaSetupBeginResponseData>("/admin/auth/mfa/setup/begin", {
      method: "POST",
      body: JSON.stringify({ mfaToken }),
    }),
  confirmMfaSetup: (payload: AdminMfaSetupConfirmRequest) =>
    unwrap<AdminMfaSetupConfirmResponseData>("/admin/auth/mfa/setup/confirm", {
      method: "POST",
      body: JSON.stringify(payload),
    }),
  refresh: (payload: AdminRefreshRequest) =>
    unwrap<AdminRefreshResponseData>("/admin/auth/refresh", {
      method: "POST",
      body: JSON.stringify(payload),
    }),
  logout: (payload: AdminLogoutRequest) =>
    unwrap<AuthActionAcceptedResponseData>("/admin/auth/logout", {
      method: "POST",
      body: JSON.stringify(payload),
    }),
  getMe: () => unwrap<AdminProfileResponseData>("/admin/me"),
};

export const adminOpsClient = {
  getDashboardSummary: () => unwrap<AdminDashboardSummaryResponseData>("/admin/dashboard/summary"),

  listUsers: (page?: number, q?: string) =>
    unwrap<AdminUserListResponseData>(`/admin/users${qs({ page, q })}`),
  getUser: (userId: string) => unwrap<AdminUserSummary>(`/admin/users/${userId}`),
  suspendUser: (userId: string, payload: AdminCriticalActionRequest) =>
    unwrap<AdminUserSummary>(`/admin/users/${userId}/suspend`, {
      method: "POST",
      body: JSON.stringify(payload),
    }),
  unsuspendUser: (userId: string, payload: AdminCriticalActionRequest) =>
    unwrap<AdminUserSummary>(`/admin/users/${userId}/unsuspend`, {
      method: "POST",
      body: JSON.stringify(payload),
    }),

  listStores: (page?: number, q?: string) =>
    unwrap<AdminStoreListResponseData>(`/admin/stores${qs({ page, q })}`),
  getStore: (storeId: string) => unwrap<AdminStoreDetailResponseData>(`/admin/stores/${storeId}`),
  verifyStore: (storeId: string, payload: AdminCriticalActionRequest) =>
    unwrap<AdminStoreDetailResponseData>(`/admin/stores/${storeId}/verify`, {
      method: "POST",
      body: JSON.stringify(payload),
    }),
  suspendStore: (storeId: string, payload: AdminCriticalActionRequest) =>
    unwrap<AdminStoreDetailResponseData>(`/admin/stores/${storeId}/suspend`, {
      method: "POST",
      body: JSON.stringify(payload),
    }),
  unsuspendStore: (storeId: string, payload: AdminCriticalActionRequest) =>
    unwrap<AdminStoreDetailResponseData>(`/admin/stores/${storeId}/unsuspend`, {
      method: "POST",
      body: JSON.stringify(payload),
    }),

  listProducts: (page?: number, q?: string) =>
    unwrap<AdminProductListResponseData>(`/admin/products${qs({ page, q })}`),
  suspendProduct: (productId: string, payload: AdminCriticalActionRequest) =>
    unwrap(`/admin/products/${productId}/suspend`, { method: "POST", body: JSON.stringify(payload) }),
  restoreProduct: (productId: string, payload: AdminCriticalActionRequest) =>
    unwrap(`/admin/products/${productId}/restore`, { method: "POST", body: JSON.stringify(payload) }),

  listBookings: (page?: number, q?: string) =>
    unwrap<AdminBookingListResponseData>(`/admin/bookings${qs({ page, q })}`),
  getBooking: (bookingId: string) =>
    unwrap<AdminBookingDetailResponseData>(`/admin/bookings/${bookingId}`),
  cancelBooking: (bookingId: string, payload: AdminCriticalActionRequest) =>
    unwrap(`/admin/bookings/${bookingId}/cancel`, { method: "POST", body: JSON.stringify(payload) }),
  releaseReservation: (bookingId: string, payload: AdminCriticalActionRequest) =>
    unwrap(`/admin/bookings/${bookingId}/release-reservation`, {
      method: "POST",
      body: JSON.stringify(payload),
    }),

  listReports: (page?: number) => unwrap<AdminReportListResponseData>(`/admin/reports${qs({ page })}`),
  getReport: (reportId: string) => unwrap<AdminReportDetailResponseData>(`/admin/reports/${reportId}`),
  assignReport: (reportId: string) =>
    unwrap(`/admin/reports/${reportId}/assign`, { method: "POST", body: "{}" }),
  resolveReport: (reportId: string, payload: AdminCriticalActionRequest) =>
    unwrap(`/admin/reports/${reportId}/resolve`, { method: "POST", body: JSON.stringify(payload) }),
  rejectReport: (reportId: string, payload: AdminCriticalActionRequest) =>
    unwrap(`/admin/reports/${reportId}/reject`, { method: "POST", body: JSON.stringify(payload) }),

  listAuditLogs: (page?: number, action?: string) =>
    unwrap<AdminAuditLogListResponseData>(`/admin/audit-logs${qs({ page, action })}`),
  listResourceAuditLogs: (resourceType: string, resourceId: string) =>
    unwrap<AdminAuditLogListResponseData>(`/admin/audit-logs${qs({ resourceType, resourceId, limit: 20 })}`),

  listContent: () => unwrap<ContentItemListResponseData>("/admin/content"),
  getContent: (id: string) => unwrap<ContentItemResponseData>(`/admin/content/${id}`),
  createContent: (payload: CreateContentItemRequest) =>
    unwrap<ContentItemResponseData>("/admin/content", { method: "POST", body: JSON.stringify(payload) }),
  updateContent: (id: string, payload: UpdateContentItemRequest) =>
    unwrap<ContentItemResponseData>(`/admin/content/${id}`, { method: "POST", body: JSON.stringify(payload) }),
  publishContent: (id: string) =>
    unwrap<ContentItemResponseData>(`/admin/content/${id}/publish`, { method: "POST" }),
  saveDraftContent: (id: string) =>
    unwrap<ContentItemResponseData>(`/admin/content/${id}/save-draft`, { method: "POST" }),
  requestContentMediaUploadUrl: (id: string, payload: RequestContentMediaUploadUrlRequest) =>
    unwrap<RequestContentMediaUploadUrlResponseData>(`/admin/content/${id}/media/upload-url`, {
      method: "POST",
      body: JSON.stringify(payload),
    }),

  adjustCredit: (storeId: string, payload: AdminCreditAdjustmentRequest, idempotencyKey: string) =>
    unwrap<AdminCreditAdjustmentResponseData>(`/admin/stores/${storeId}/credit-adjustments`, {
      method: "POST",
      body: JSON.stringify(payload),
      headers: { "Idempotency-Key": idempotencyKey },
    }),

  listCreditPackages: () => unwrap<CreditPackageResponseData[]>("/admin/credit-packages"),
  listSubscriptionPlans: () => unwrap<SubscriptionPlanResponseData[]>("/admin/subscription-plans"),
  listPromotionServices: () => unwrap<PromotionServiceResponseData[]>("/admin/promotion-services"),
  listWebhookEvents: (page?: number) =>
    unwrap<AdminWebhookEventListResponseData>(`/admin/webhook-events${qs({ page })}`),
};
